import { useCallback } from "react"
import { graphql, UseMutationConfig } from "react-relay"
import i18next from "i18next"
import { type CustomMutationParams, useMutationWithErrorDialog } from "@/hooks/useMutationWithErrorDialog"
import {
  Ch_generateMeasRadiologyPrescriptionDocumentInput,
  useChGenerateRadiologyPrescriptionDocumentMutation as useChGenerateRadiologyPrescriptionDocumentMutationType,
} from "@/modules/patients/detail/meas-radiology-prescription/ch/services/__generated__/useChGenerateRadiologyPrescriptionDocumentMutation.graphql"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"

const generateRadiologyPrescriptionDocumentMutation = graphql`
  mutation useChGenerateRadiologyPrescriptionDocumentMutation(
    $input: Ch_generateMeasRadiologyPrescriptionDocumentInput!
  ) {
    ch_generateMeasRadiologyPrescriptionDocument(input: $input) {
      ch_MeasRadiologyPrescription {
        id
        fileId
        ...chMeasRadiologyPrescriptionFragment
      }
      errors {
        ... on Error {
          message
        }
      }
    }
  }
`

type Config = Omit<UseMutationConfig<useChGenerateRadiologyPrescriptionDocumentMutationType>, "variables"> & {
  id: string
  requestExaminations: Ch_generateMeasRadiologyPrescriptionDocumentInput["requestExaminations"]
  uploadSessionCode?: Ch_generateMeasRadiologyPrescriptionDocumentInput["uploadSessionCode"]
}

const useChGenerateRadiologyPrescriptionDocumentMutation = (params?: CustomMutationParams) => {
  const [commit, isFlight] = useMutationWithErrorDialog<useChGenerateRadiologyPrescriptionDocumentMutationType>(
    generateRadiologyPrescriptionDocumentMutation,
    params,
  )
  const patientById = usePatientParam()
  const de = i18next.getFixedT("de")

  const commitHandler = useCallback(
    async (config: Config) => {
      const { id, requestExaminations, uploadSessionCode } = config

      commit({
        variables: {
          input: {
            id,
            patientId: patientById,
            uploadSessionCode,
            requestExaminations: requestExaminations.map((item) =>
              de(`patient.measRadiologyPrescription.requestExaminations.${item}`),
            ),
          },
        },
        ...config,
      })
    },
    [commit, patientById, de],
  )

  return [commitHandler, isFlight] as const
}

export { useChGenerateRadiologyPrescriptionDocumentMutation }
